/**
 * @claimshield/marketing — Vue app entry point.
 *
 * Serves: apply.claimshieldpro.com (driver application, signing flows,
 * /get-quote insurance flow, auth views).
 *
 * NOT the public marketing site. The marketing site is the Astro
 * project (in Hydra at src/marketing-site/) which deploys separately to the
 * claimshield-website Cloudflare Pages project.
 *
 * See src/platform/packages/marketing/CLAUDE.md for context.
 */
import { createApp } from 'vue'
import { createPinia } from 'pinia'

// PrimeVue
import PrimeVue from 'primevue/config'
import Aura from '@primevue/themes/aura'
import { definePreset } from '@primevue/themes'
import ToastService from 'primevue/toastservice'
import Tooltip from 'primevue/tooltip'

// PrimeVue components (lighter set for driver application + insurance quoting forms)
import Button from 'primevue/button'
import InputText from 'primevue/inputtext'
import Card from 'primevue/card'
import Toast from 'primevue/toast'
import Calendar from 'primevue/calendar'
import Dropdown from 'primevue/dropdown'
import InputMask from 'primevue/inputmask'
import InputNumber from 'primevue/inputnumber'
import Checkbox from 'primevue/checkbox'
import Textarea from 'primevue/textarea'
import Divider from 'primevue/divider'
import Message from 'primevue/message'
import ProgressBar from 'primevue/progressbar'

// App and router
import App from './App.vue'
import { router, attemptChunkReload, isChunkLoadErrorMessage } from './router'
import { installErrorCapture } from '@claimshield/shared'

// Styles
import '@claimshield/shared/styles'
import 'primeicons/primeicons.css'
import './styles.css' // app-wide light color-scheme (fixes dark inputs in OS dark-mode)

// Create Vue app
const app = createApp(App)

// Pinia state management
const pinia = createPinia()
app.use(pinia)

// Aura ships an emerald primary ramp. Left unconfigured — as this app was until
// 2026-08-11 — every PrimeVue control (Button, Checkbox, Calendar selection,
// Dropdown highlight, ProgressBar, focus ring) renders green inside a
// ClaimShield-purple UI, because Tailwind's `primary-*` and PrimeVue's
// `--p-primary-*` are two unrelated scales. The ramp below mirrors this
// package's own tailwind.config.js → colors.primary exactly.
//
// The ramp alone is not enough. Aura resolves `primary.color` to `{primary.500}`,
// but the brand anchor is #3d2683, which is 700 on our scale — so a ramp-only
// preset ships purple-500 (#7952b3) as the action colour and leaves the anchor
// unused. Pin the colorScheme so every primary-derived surface lands on the
// anchor. Same block as packages/ops/src/main.ts; keep the four in sync
// (ops, portal, driver-portal, marketing).
const ClaimShieldAura = definePreset(Aura, {
  semantic: {
    primary: {
      50: '#f5f3fa',
      100: '#ebe6f5',
      200: '#d4c9eb',
      300: '#b8a3dc',
      400: '#9575c9',
      500: '#7952b3',
      600: '#6b4faf',
      700: '#3d2683',
      800: '#2a1a5e',
      900: '#1c1240',
      950: '#0e0920',
    },
    colorScheme: {
      light: {
        primary: {
          color: '{primary.700}',
          contrastColor: '#ffffff',
          hoverColor: '{primary.800}',
          activeColor: '{primary.900}',
        },
      },
    },
  },
})

// PrimeVue with Aura theme, re-pointed at the ClaimShield purple scale
app.use(PrimeVue, {
  theme: {
    preset: ClaimShieldAura,
    options: {
      prefix: 'p',
      darkModeSelector: '.dark-mode',
      cssLayer: {
        name: 'primevue',
        order: 'tailwind-base, primevue, tailwind-utilities',
      },
    },
  },
  ripple: true,
})

// PrimeVue services
app.use(ToastService)

// PrimeVue directive
app.directive('tooltip', Tooltip)

// Register PrimeVue components globally
app.component('PButton', Button)
app.component('PInputText', InputText)
app.component('PCard', Card)
app.component('PToast', Toast)
app.component('PCalendar', Calendar)
app.component('PDropdown', Dropdown)
app.component('PInputMask', InputMask)
app.component('PInputNumber', InputNumber)
app.component('PCheckbox', Checkbox)
app.component('PTextarea', Textarea)
app.component('PDivider', Divider)
app.component('PMessage', Message)
app.component('PProgressBar', ProgressBar)

// Router
app.use(router)

// Global JS error capture — must run after Pinia is registered.
installErrorCapture(app)

// Backstop for chunk-load failures not surfaced via router.onError —
// e.g. lazy imports triggered from non-route components, or raw <script>
// fetch failures. Shares the loop guard with the router-level handler.
window.addEventListener(
  'error',
  (event) => {
    const tgt = event.target as HTMLElement | null
    if (
      tgt &&
      tgt.tagName === 'SCRIPT' &&
      (tgt as HTMLScriptElement).src?.includes('/assets/')
    ) {
      attemptChunkReload()
    }
  },
  true, // capture phase — script error events don't bubble
)

window.addEventListener('unhandledrejection', (event) => {
  const reason = event.reason
  const msg = reason instanceof Error ? reason.message : String(reason)
  if (isChunkLoadErrorMessage(msg)) {
    attemptChunkReload()
  }
})

// Mount
app.mount('#app')
